import { useState } from 'react'
import { ChevronDown, Filter, X } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'

export interface FootageFilters {
  contentRole?: string
  actionType?: string
  quality?: string
  shotSize?: string
  pointOfView?: string
  voice?: string
  sortBy: 'newest' | 'oldest' | 'alphabetical'
}

interface FootageFilterSidebarProps {
  filters: FootageFilters
  onChange: (filters: FootageFilters) => void
  className?: string
}

interface FilterGroup {
  key: keyof Omit<FootageFilters, 'sortBy'>
  label: string
  options: { value: string; label: string }[]
}

const FILTER_GROUPS: FilterGroup[] = [
  {
    key: 'contentRole',
    label: 'Action Type',
    options: [
      { value: 'hook', label: 'Hook' },
      { value: 'pain', label: 'Pain' },
      { value: 'solution', label: 'Solution' },
      { value: 'cta', label: 'CTA' },
      { value: 'lifestyle', label: 'Lifestyle' },
      { value: 'ugc', label: 'UGC' },
      { value: 'transition', label: 'Transition' },
      { value: 'product_demo', label: 'Product Demo' },
      { value: 'testimonial', label: 'Testimonial' },
      { value: 'unboxing', label: 'Unboxing' },
    ],
  },
  {
    key: 'quality',
    label: 'Quality',
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
  },
  {
    key: 'shotSize',
    label: 'Shotsize',
    options: [
      { value: 'close_up', label: 'Close Up' },
      { value: 'medium', label: 'Medium' },
      { value: 'wide', label: 'Wide' },
      { value: 'extreme_close_up', label: 'Extreme Close Up' },
      { value: 'overhead', label: 'Overhead' },
    ],
  },
  {
    key: 'pointOfView',
    label: 'Point Of View',
    options: [
      { value: 'first_person', label: 'First Person' },
      { value: 'third_person', label: 'Third Person' },
      { value: 'pov_mirror', label: 'POV Mirror' },
      { value: 'overhead', label: 'Overhead' },
    ],
  },
  {
    key: 'voice',
    label: 'Voice',
    options: [
      { value: 'voiceover', label: 'Voiceover' },
      { value: 'on_camera', label: 'On Camera' },
      { value: 'text_only', label: 'Text Only' },
      { value: 'silent', label: 'Silent / No Voice' },
    ],
  },
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'alphabetical', label: 'Alphabetical' },
] as const

export function FootageFilterSidebar({ filters, onChange, className }: FootageFilterSidebarProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    FILTER_GROUPS.reduce(
      (acc, g) => ({ ...acc, [g.key]: true }),
      { sorting: true } as Record<string, boolean>,
    ),
  )

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleFilterSelect = (groupKey: keyof Omit<FootageFilters, 'sortBy'>, value: string) => {
    onChange({
      ...filters,
      [groupKey]: filters[groupKey] === value ? undefined : value,
    })
  }

  const hasActiveFilters = FILTER_GROUPS.some((g) => filters[g.key])

  const clearAll = () => {
    onChange({
      sortBy: filters.sortBy,
    })
  }

  return (
    <aside className={cn('w-56 flex-shrink-0 border-r border-tf-border overflow-y-auto', className)}>
      <div className="px-3 py-3 flex items-center justify-between border-b border-tf-border">
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-tf-text-muted" />
          <span className="text-sm font-semibold text-tf-text">Filters</span>
        </div>
      </div>

      {/* Sorting */}
      <div className="border-b border-tf-border">
        <button
          type="button"
          onClick={() => toggleGroup('sorting')}
          className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-tf-bg-muted transition-colors"
        >
          <span className="text-sm font-medium text-tf-text">Sorting</span>
          <ChevronDown
            className={cn(
              'w-4 h-4 text-tf-text-muted transition-transform',
              !expandedGroups.sorting && '-rotate-90',
            )}
          />
        </button>
        {expandedGroups.sorting && (
          <div className="px-3 pb-3">
            <select
              value={filters.sortBy}
              onChange={(e) =>
                onChange({ ...filters, sortBy: e.target.value as FootageFilters['sortBy'] })
              }
              className="w-full px-2 py-1.5 border border-tf-border rounded text-xs bg-tf-bg-card text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Filter groups */}
      {FILTER_GROUPS.map((group) => (
        <div key={group.key} className="border-b border-tf-border">
          <button
            type="button"
            onClick={() => toggleGroup(group.key)}
            className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-tf-bg-muted transition-colors"
          >
            <span className="text-sm font-medium text-tf-text">{group.label}</span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-tf-text-muted transition-transform',
                !expandedGroups[group.key] && '-rotate-90',
              )}
            />
          </button>
          {expandedGroups[group.key] && (
            <div className="px-3 pb-3 space-y-1">
              {group.options.map((opt) => {
                const isSelected = filters[group.key] === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleFilterSelect(group.key, opt.value)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 rounded text-xs transition-colors',
                      isSelected
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-tf-text-sub hover:bg-tf-bg-hover',
                    )}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ))}

      {/* Clear all */}
      {hasActiveFilters && (
        <div className="px-3 py-3">
          <button
            type="button"
            onClick={clearAll}
            className="flex items-center gap-1.5 text-xs text-tf-text-muted hover:text-tf-text transition-colors"
          >
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}
    </aside>
  )
}

export default FootageFilterSidebar
